/**
 * CE QUE L'ÉCRAN « ARTICLES À VALIDER » DÉCIDE AVANT D'APPELER LE SERVEUR — règles pures, testables.
 *
 * UTILITÉ : trois questions se posent AVANT le clic, et chacune se trompe silencieusement si on la
 * laisse dans le JSX — quelle décision proposer d'emblée, laquelle est impossible et pourquoi, et
 * quelle cible de fusion pré-remplir.
 *
 * ⚠️ CE MODULE NE DÉCIDE PAS À LA PLACE DU SERVEUR. C'est lui qui refuse (`sold_without_gencode`) et
 * son refus reste la vérité ; on rejoue la règle ici uniquement pour la DIRE avant le clic. Offrir un
 * bouton qui va échouer serait une option offerte non implémentée (§23.0) — et le griser sans
 * expliquer pourquoi serait pire encore : l'opérateur croirait à un droit manquant.
 */

/** Le minimum d'un brouillon dont dépendent les règles ci-dessous. */
import { parseMinor } from '@orgazone/money-format';
import { t } from '../i18n';
export interface DraftForDecision {
  /**
   * D'où vient le brouillon.
   *
   * ⚠️ SEUL `sale` CHANGE QUELQUE CHOSE ICI, et c'est pour ça que la liste peut s'allonger sans
   * risque : la règle bloquante tient à « des lignes de vente sont déjà scellées ». Un brouillon né
   * d'un menu ou d'un terminal de stock n'a rien vendu, donc il se valide de la même façon.
   * ⚠️ ELLE DOIT SUIVRE `ArticleDraft.origin` : c'est la même donnée, lue par deux types. En élargir
   * un sans l'autre fait tomber l'écran au typecheck — ce qui est arrivé, et c'est le bon symptôme.
   */
  origin: 'sale' | 'menu' | 'mobile-partner';
  gencode: string | null;
  duplicate: { articleId: string; certain: boolean; archived?: boolean } | null;
}

/** Les trois décisions offertes. */
export type DraftDecision = 'valider' | 'fusionner' | 'refuser';

/**
 * POURQUOI CE BROUILLON NE PEUT PAS DEVENIR UN ARTICLE — ou `null` s'il le peut.
 *
 * ⚠️ LE CAS EST ÉTROIT ET IL COMPTE : vendu, sans code produit. Ce n'est pas la signature qui
 * l'interdit (le gencode n'entre pas dans la charge signée §2.3), c'est **R13**, qui exige le code
 * produit au niveau ligne — sur une `fiscal_piece_line` scellée append-only, un code posé après coup
 * est impossible. Un brouillon `menu` sans code, lui, se valide très bien : rien n'a encore été vendu,
 * le code interne est attribué à la validation.
 */
export function blockingReason(d: DraftForDecision): string | null {
  if (d.origin === 'sale' && !d.gencode) {
    return t('Vendu sans code produit — ses lignes de vente sont scellées et le code ne peut plus y être ajouté (R13). Il ne deviendra pas un article : la garde de la caisse a cédé, signalez-le.');
  }
  return null;
}

/**
 * La décision à présenter EN PREMIER.
 *
 * ⚠️ UN DOUBLON CERTAIN OUVRE SUR « FUSIONNER », et c'est le seul cas où l'écran oriente : le
 * code-barres est le même, valider créerait un second article pour un produit qui en a déjà un. Une
 * simple ressemblance de libellé n'oriente RIEN — présenter une hypothèse comme une évidence ferait
 * fusionner deux articles réels.
 */
export function defaultDecision(d: DraftForDecision): DraftDecision {
  if (blockingReason(d)) return 'refuser';
  return d.duplicate?.certain ? 'fusionner' : 'valider';
}

/**
 * POURQUOI « VALIDER » N'EST PAS OFFERT — le motif bloquant, ou le doublon CERTAIN.
 *
 * ⚠️ UN CODE-BARRES DÉJÀ PORTÉ NE FAIT PAS UN SECOND ARTICLE : le serveur le refuse (`gencode_taken`), l'écran le dit
 *    avant le clic. Ce n'est pas un motif de REFUS du brouillon : la décision proposée reste « Fusionner ».
 */
export function validationBlockedReason(d: DraftForDecision): string | null {
  return blockingReason(d)
    ?? (d.duplicate?.certain ? t('Ce code-barres porte déjà un article : rattachez le brouillon à lui plutôt que d’en créer un second.') : null);
}

/**
 * LE NOM DU RATTACHEMENT — « Remettre en service » quand la cible est ARCHIVÉE, puisque c'est ce qu'il fait (décision
 * utilisateur) : la caisse n'a plus l'article, le rattachement le fait redescendre.
 */
export function mergeLabel(d: DraftForDecision): string {
  return d.duplicate?.certain && d.duplicate.archived ? t('Remettre en service') : t('Fusionner');
}

/**
 * L'article vers lequel pré-remplir la fusion — vide si rien ne le justifie.
 *
 * ⚠️ SUR UNE CERTITUDE SEULEMENT. Un champ pré-rempli se valide sans être relu : y mettre une
 * hypothèse revient à la faire accepter par défaut.
 */
export function prefilledMergeTarget(d: DraftForDecision): string {
  return d.duplicate?.certain ? d.duplicate.articleId : '';
}

/**
 * Les centimes d'un prix saisi à la main, ou `null` si ce n'est pas un nombre.
 *
 * ⚠️ `null` N'EST PAS ZÉRO. Un champ vidé, une virgule seule, un « 12,» en cours de frappe : rendre 0
 * enverrait au serveur un article à 0 € que personne n'a voulu. Le `null` désarme le bouton, il ne
 * fabrique pas une valeur.
 */
export function parsePriceCents(saisie: string): number | null {
  // ⚠️ PAR LA PORTE UNIQUE `parseMinor`, à l'échelle de la devise du magasin : la lecture écrite ici multipliait par cent
  //    au flottant et ARRONDISSAIT « 10,005 » en 10,01 — un prix que personne n'a tapé —, et dans un magasin en yens elle
  //    aurait écrit cent fois trop. Une décimale de trop se REFUSE, un prix négatif aussi.
  const lu = parseMinor(saisie);
  return lu.state === 'ok' && lu.minor >= 0 ? lu.minor : null;
}
